import { Component, ChangeDetectionStrategy, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import {
  IncomeTaxCalculatorService, IncomeTaxResult, IncomeTaxInputs, AgeCategory, DisabilityLevel,
  INCOME_TAX_CONSTRAINTS
} from './income-tax-calculator.service';

const OPTIONAL_FIELDS = [
  'interestIncome', 'otherIncome', 'rentalIncome', 'homeLoanInterestLetOut',
  'hraExemption', 'section80C', 'section80CCD1B', 'section80D',
  'homeLoanInterestSelfOccupied', 'section80E', 'section80G',
  'rentPaid', 'section80EEA', 'section80EEB', 'section80DDB',
  'professionalTax', 'ltaExemption', 'section80CCD2'
] as const;

@Component({
  selector: 'app-income-tax-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './income-tax-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./income-tax-calculator.scss']
})
export class IncomeTaxCalculator {
  fmt = inject(FormatService);
  private calc = inject(IncomeTaxCalculatorService);


  readonly C = INCOME_TAX_CONSTRAINTS;
  readonly Math = Math;

  ageCategory: AgeCategory = 'below60';

  annualSalary = 1_200_000;
  interestIncome = 0;
  otherIncome = 0;
  rentalIncome = 0;
  homeLoanInterestLetOut = 0;

  hraExemption = 0;
  section80C = 150_000;
  section80CCD1B = 0;
  section80D = 0;
  homeLoanInterestSelfOccupied = 0;
  section80E = 0;
  section80G = 0;
  rentPaid = 0;
  section80EEA = 0;
  section80EEB = 0;
  section80DD: DisabilityLevel = 'none';
  section80U: DisabilityLevel = 'none';
  section80DDB = 0;
  professionalTax = 0;
  ltaExemption = 0;
  section80CCD2 = 0;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:       IncomeTaxResult | null = null;
  showBreakup = false;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.annualSalary, this.C.annualSalary.min, this.C.annualSalary.max, 'Annual salary income');
    if (e1) errors['annualSalary'] = e1;

    for (const field of OPTIONAL_FIELDS) {
      const c = this.C[field];
      const err = validateRangeAllowZero(this[field], c.min, c.max, this.fieldLabel(field));
      if (err) errors[field] = err;
    }

    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  private fieldLabel(field: string): string {
    const labels: Record<string, string> = {
      interestIncome: 'Interest income', otherIncome: 'Other income',
      rentalIncome: 'Rental income', homeLoanInterestLetOut: 'Home loan interest (let-out)',
      hraExemption: 'HRA exemption', section80C: 'Section 80C', section80CCD1B: 'Section 80CCD(1B)',
      section80D: 'Section 80D', homeLoanInterestSelfOccupied: 'Home loan interest (self-occupied)',
      section80E: 'Section 80E', section80G: 'Section 80G',
      rentPaid: 'Rent paid', section80EEA: 'Section 80EEA', section80EEB: 'Section 80EEB',
      section80DDB: 'Section 80DDB', professionalTax: 'Professional tax',
      ltaExemption: 'LTA exemption', section80CCD2: 'Section 80CCD(2)'
    };
    return labels[field] ?? field;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  setAgeCategory(age: AgeCategory): void {
    this.ageCategory = age;
    if (this.calculated) this.calculated = false;
  }

  setDisabilityDependent(level: DisabilityLevel): void {
    this.section80DD = level;
    if (this.calculated) this.calculated = false;
  }

  setDisabilitySelf(level: DisabilityLevel): void {
    this.section80U = level;
    if (this.calculated) this.calculated = false;
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) this.calculated = false;
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { annualSalary: true };
    for (const field of OPTIONAL_FIELDS) this.touched[field] = true;
    if (!this.validateForm()) return;

    const inputs: IncomeTaxInputs = {
      ageCategory: this.ageCategory,
      annualSalary: this.annualSalary,
      interestIncome: this.interestIncome,
      otherIncome: this.otherIncome,
      rentalIncome: this.rentalIncome,
      homeLoanInterestLetOut: this.homeLoanInterestLetOut,
      hraExemption: this.hraExemption,
      section80C: this.section80C,
      section80CCD1B: this.section80CCD1B,
      section80D: this.section80D,
      homeLoanInterestSelfOccupied: this.homeLoanInterestSelfOccupied,
      section80E: this.section80E,
      section80G: this.section80G,
      rentPaid: this.rentPaid,
      section80EEA: this.section80EEA,
      section80EEB: this.section80EEB,
      section80DD: this.section80DD,
      section80U: this.section80U,
      section80DDB: this.section80DDB,
      professionalTax: this.professionalTax,
      ltaExemption: this.ltaExemption,
      section80CCD2: this.section80CCD2
    };

    this.result = this.calc.calculate(inputs);
    this.calculated = true;
  }

  toggleBreakup(): void {
    this.showBreakup = !this.showBreakup;
  }

  reset(): void {
    this.ageCategory                  = 'below60';
    this.annualSalary                 = 1_200_000;
    this.interestIncome               = 0;
    this.otherIncome                  = 0;
    this.rentalIncome                 = 0;
    this.homeLoanInterestLetOut       = 0;
    this.hraExemption                 = 0;
    this.section80C                   = 150_000;
    this.section80CCD1B               = 0;
    this.section80D                   = 0;
    this.homeLoanInterestSelfOccupied = 0;
    this.section80E                   = 0;
    this.section80G                   = 0;
    this.rentPaid                     = 0;
    this.section80EEA                 = 0;
    this.section80EEB                 = 0;
    this.section80DD                  = 'none';
    this.section80U                   = 'none';
    this.section80DDB                 = 0;
    this.professionalTax              = 0;
    this.ltaExemption                 = 0;
    this.section80CCD2                = 0;
    this.formErrors                   = {};
    this.touched                      = {};
    this.showFormErrors               = false;
    this.calculated                   = false;
    this.result                       = null;
    this.showBreakup                  = false;
  }
}
